import * as React from 'react'
import { CurrencyDollar } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { Card, CardHeader, CardTitle, CardAction, CardContent } from '../core/card'
import { type PeriodValue } from '../core/period-picker'
import {
  LineChart,
  PerformanceControls,
  type LineChartData,
} from './performance-charts'

/**
 * ValueTrendWidget — deal value over time as a multi-series line chart (e.g.
 * Value Opened vs Value Won, on a `$` axis). Wraps the shared {@link LineChart};
 * hover a point for its value; the chart fills the card height.
 *
 *   <ValueTrendWidget data={{ labels, series, leftFormat }} onPeriodApply={reload} />
 */

export interface ValueTrendWidgetProps {
  data: LineChartData
  periodLabel?: React.ReactNode
  defaultPeriod?: PeriodValue
  onPeriodApply?: (value: PeriodValue) => void
  /** Wire the ⋯ menu's "Hide widget" action; hidden when omitted. */
  onHide?: () => void
  title?: React.ReactNode
  className?: string
}

export const ValueTrendWidget = React.forwardRef<
  HTMLDivElement,
  ValueTrendWidgetProps
>(
  (
    { data, periodLabel = 'Period', defaultPeriod, onPeriodApply, onHide, title = 'Value Trend', className },
    ref
  ) => (
    <Card ref={ref} className={cn('flex w-full max-w-2xl flex-col', className)}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CurrencyDollar aria-hidden="true" className="size-4 text-fbui-gray-500" />
          {title}
        </CardTitle>
        <CardAction>
          <PerformanceControls
            periodLabel={periodLabel}
            defaultPeriod={defaultPeriod}
            onPeriodApply={onPeriodApply}
            onHide={onHide}
            hideLabel="Value trend options"
          />
        </CardAction>
      </CardHeader>
      <CardContent className="flex min-h-0 flex-1 flex-col">
        <LineChart data={data} />
      </CardContent>
    </Card>
  )
)
ValueTrendWidget.displayName = 'ValueTrendWidget'
